import { Map } from "immutable";

import { immutableRecords } from "../../../../types";
import { keyArrayFromMap, keySetFromMap } from "../../../../utils/immutableExtensions";
import { appStateSelectors } from "../../../appState";
import { attachmentSelectors } from "../../../attachments";
import { characterWidthMapSelectors } from "../../../characterWidthMap";
import { dateConstraintSelectors } from "../../../dateConstraints";
import { definitionSelectors } from "../../../definition";
import { calendarSelectors } from "../../../definition/calendar";
import { colorSelectors } from "../../../definition/colors";
import { propertySelectors } from "../../../definition/properties";
import { subwayViewSettingsSelectors } from "../../../definition/subwayViewSettings";
import { typeSelectors } from "../../../definition/types";
import { dependencySelectors } from "../../../dependencies";
import { itemSelectors } from "../../../items";
import { mobileScreenSelectors } from "../../../mobileScreen";
import { narrativeSelectors } from "../../../narrative";
import { relationshipSelectors } from "../../../relationships";
import { sequenceSelectors } from "../../../sequence";
import { superAndChildrenSelectors } from "../../../superAndChildrenById";
import { uiStateSelectors } from "../../../uiState";
import applyColorUpdates from "./applyColorUpdates";
import applyImagePathUpdates from "./applyImageUpdates";
import applyNarrativeUpdates from "./applyNarrativeUpdates";
import applySequenceUpdates from "./applySequenceUpdates";
import applySuperUpdates from "./applySuperUpdates";
import mergeChangedItemDates from "./mergeChangedItemDates";
import mergeChangedItems from "./mergeChangedItems";
import mergeChangedSuperAndChildren from "./mergeChangedSuperAndChildren";
import updateBlockIdentifiers from "./updateBlockIdentifiers";
import updateIsViolated from "./updateIsViolated";
import updateItemComplete from "./updateItemComplete";
import updateItemInitials from "./updateItemInitials";
import updateItemTextSizes from "./updateItemTextSizes";
import updateRelatedItemsByType from "./updateRelatedItemsByType";

// Cases to handle:

// To delete ... ?
//   - isFiltered <-- surely this is just leftover unused crud?
//   - selected

// Ideally remove from ItemRecord:
//   - super
//   - children

// Identifiers for blocks/blocked by
//   - blockingIdentifiers: List(),
//   - blockedByIdentifiers: List(),

// Identifiers for related items
//   - relatedItemsByType: List(),

// Narrative data: (input: narrative.. what has moved, and what is affected by that (typically things after))
//   - narrativePosition
//   - narrativeLabel
//   - narrativeAutoLabel // the manual or auto-calculated label
//   - narrativeDisplayLabel // the full string we will display
//   - narrativePathLabel // full string of all parents and this item

// Sequence/Nesting data (input: sequence, superAndChildrenById)
//   - sequenceIndex
//   - nestedIndex
//   - superDepth

// Text Calculations (input: item texts, font size, character widths, etc.)
//   - titleMetrics: TextMetricsRecord(),
//   - nestedTitleMetrics: TextMetricsRecord(),
//   - narrativeNestedTitleMetrics: TextMetricsRecord(),
//   - subwayTitleMetrics: TextMetricsRecord(),
//   - itemCardNestedTitleMetrics: TextMetricsRecord(),
//   - itemCardNarrativeNestedTitleMetrics: TextMetricsRecord(),
//   - itemCardNestedSummaryMetrics: TextMetricsRecord(),
//   - itemCardNarrativeNestedSummaryMetrics: TextMetricsRecord(),
//   - summaryMetrics: TextMetricsRecord(),
//   - dateTextSize: TextSizeRecord(),
//   - durationTextSize: TextSizeRecord(),
//   - narrativeTextSize: TextSizeRecord(),
//   - identifierSize: SizeRecord(),

// This should be separated out per view-type
//   - Card Size (inputs: text calculations, view display settings)

// This should be separated out per individual view
//   - excludedByFilter
//   - index (used by Table, Relationship, Spreadsheet)
//   - selected: false,

// From item allIds
//   - index

// From Properties (input: item.propertyValues, propertiesById)
//   - isCompleted

// Tags ??? <-- do we need to convert these from IDs to text or anything?

// From self (input: item.image and item.attachments)
//   - imagePath

// Violated constraints (input: item.dates, item.dateConstraints, item.blockingDependencies, item.blockedByDependencies, other item dates)
//   - hasViolatedConstraints

const reducer = (state = Map(), action, newGlobalState, prevGlobalState) => {
  // If we have no data loaded yet, don't bother deriving anything
  if (!newGlobalState.isDataLoaded) {
    return state;
  }
  // TODO: Need to handle the root action types that mean calculate everything for everyone always!

  // If none of the things that might wind up affecting ItemDisplayRecord have changed, then
  // we can bail

  const todayDate = newGlobalState.display.lastTodayDate;

  // Grab some stuff we will need
  const newItemsById = itemSelectors.getItemsById(newGlobalState);
  const prevItemsById = itemSelectors.getItemsById(prevGlobalState);

  const newRelationshipsById = relationshipSelectors.getRelationshipsById(newGlobalState);
  const prevRelationshipsById = relationshipSelectors.getRelationshipsById(prevGlobalState);

  const newDependenciesById = dependencySelectors.getDependenciesById(newGlobalState);
  const prevDependenciesById = dependencySelectors.getDependenciesById(prevGlobalState);

  const newConstraintsById = dateConstraintSelectors.getDateConstraintsById(newGlobalState);
  const prevConstraintsById = dateConstraintSelectors.getDateConstraintsById(prevGlobalState);

  const newSuperAndChildrenById = superAndChildrenSelectors.getSuperAndChildrenById(newGlobalState);
  const prevSuperAndChildrenById = superAndChildrenSelectors.getSuperAndChildrenById(prevGlobalState);

  const newNarrative = narrativeSelectors.getNarrativeTree(newGlobalState);
  const prevNarrative = narrativeSelectors.getNarrativeTree(prevGlobalState);

  const newSequence = sequenceSelectors.getItemSequence(newGlobalState);
  const prevSequence = sequenceSelectors.getItemSequence(prevGlobalState);

  const newPropertiesById = propertySelectors.getPropertiesById(newGlobalState);
  const newTypesById = typeSelectors.getTypesById(newGlobalState);
  const prevTypesById = typeSelectors.getTypesById(prevGlobalState);
  const newColorsById = colorSelectors.getColorsById(newGlobalState);

  const newCalendar = calendarSelectors.getCalendar(newGlobalState);
  const prevCalendar = calendarSelectors.getCalendar(prevGlobalState);

  const newFontSize = appStateSelectors.getFontSize(newGlobalState);
  const prevFontSize = appStateSelectors.getFontSize(prevGlobalState);
  const newWidthMap = characterWidthMapSelectors.getCharacterWidthMap(newGlobalState);
  const prevWidthMap = characterWidthMapSelectors.getCharacterWidthMap(prevGlobalState);
  const newSubwayLineLimit = subwayViewSettingsSelectors.getSubwayViewSettingsLineCountNumericLimit(newGlobalState);
  const prevSubwayLineLimit = subwayViewSettingsSelectors.getSubwayViewSettingsLineCountNumericLimit(prevGlobalState);

  let newMobileWidth = null;
  let prevMobileWidth = null;
  let newIsMobilePortraitMode = false;
  let prevIsMobilePortraitMode = false;
  let newMobileShowDragHandles = false;
  let prevMobileShowDragHandles = false;

  //#!if mobile === true || harvard === true
  newMobileWidth = mobileScreenSelectors.getMobileScreenWidth(newGlobalState);
  prevMobileWidth = mobileScreenSelectors.getMobileScreenWidth(prevGlobalState);
  newIsMobilePortraitMode = mobileScreenSelectors.getIsMobilePortraitMode(newGlobalState);
  prevIsMobilePortraitMode = mobileScreenSelectors.getIsMobilePortraitMode(prevGlobalState);
  //#!endif

  //#!if mobile === true
// newMobileShowDragHandles = uiStateSelectors.getIsEditMode(newGlobalState);
// prevMobileShowDragHandles = uiStateSelectors.getIsEditMode(prevGlobalState);
  //#!endif

  const isDataChanged =
    newItemsById !== prevItemsById ||
    newRelationshipsById !== prevRelationshipsById ||
    newNarrative !== prevNarrative ||
    newSequence !== prevSequence ||
    newDependenciesById !== prevDependenciesById ||
    newConstraintsById !== prevConstraintsById ||
    newSuperAndChildrenById !== prevSuperAndChildrenById ||
    newGlobalState.data.attachments !== prevGlobalState.data.attachments;

  // Other changes of interest
  const isDefinitionsChanged =
    definitionSelectors.getEnabledDefinitions(newGlobalState) !==
      definitionSelectors.getEnabledDefinitions(prevGlobalState) || newCalendar !== prevCalendar;

  const isTextRecalcRequired =
    newFontSize !== prevFontSize ||
    newWidthMap !== prevWidthMap ||
    newSubwayLineLimit !== prevSubwayLineLimit ||
    newMobileWidth !== prevMobileWidth ||
    newIsMobilePortraitMode !== prevIsMobilePortraitMode ||
    newMobileShowDragHandles !== prevMobileShowDragHandles;

  // TODO: Are viewSettings relevant?
  // They are for calculating item cards, but we probably don't want to do that here do we?

  if (!isDataChanged && !isDefinitionsChanged && !isTextRecalcRequired) {
    return state;
  }

  let newState = state;
  const prevState = prevGlobalState.display.displayItems;
  const newAndChangedIdSet = keySetFromMap(newItemsById.filter((item, id) => item !== prevItemsById.get(id)));

  if (isDataChanged || isDefinitionsChanged) {
    const brandNewItems = newItemsById.filter((item, id) => !prevItemsById.has(id));
    const deletedIds = keyArrayFromMap(prevItemsById.filter((item, id) => !newItemsById.has(id)));
    // changedItems here contains only changed items, not new items
    const changedItems = newItemsById.filter((item, id) => prevItemsById.has(id) && item !== prevItemsById.get(id));

    // Remove any that should not exist anymore!
    if (deletedIds.length) {
      newState = newState.deleteAll(deletedIds);
    }

    if (brandNewItems.size) {
      newState = newState.merge(brandNewItems.map(i => immutableRecords.ItemDisplayRecord(i)));
    }

    if (changedItems.size) {
      newState = mergeChangedItems(newState, changedItems);
    }

    mergeChangedSuperAndChildren(newState, newSuperAndChildrenById, prevSuperAndChildrenById);

    // Update denormalised color values
    newState = applyColorUpdates(newState, action, newItemsById, newColorsById);

    // For now... merge in the ItemDates records... at some point we probably won't do this anymore!
    const newItemDates = newGlobalState.display.itemDates;
    const prevItemDates = prevGlobalState.display.itemDates;

    if (newItemDates !== prevItemDates) {
      newState = mergeChangedItemDates(newState, newItemDates, prevItemDates);
    }

    // Update derived super values
    newState = applySuperUpdates(newState, action, newSuperAndChildrenById, prevSuperAndChildrenById);

    // Narrative Data
    // (narrativePosition, narrativeLabel, narrativeAutoLabel, narrativeDisplayLabel, narrativePathLabel)
    newState = applyNarrativeUpdates(
      newState,
      action,
      newNarrative,
      prevNarrative,
      newItemsById,
      typeSelectors.getNarrativeFolderTypeId(newGlobalState),
      definitionSelectors.getNarrativeSettings(newGlobalState)
    );

    // Index + Sequence + Nesting Position Data
    // (index, sequenceIndex, nestedIndex, superDepth)
    newState = applySequenceUpdates(
      newState,
      action,
      newSequence,
      prevSequence,
      newSuperAndChildrenById,
      prevSuperAndChildrenById
    );

    // Image Path
    newState = applyImagePathUpdates(
      newState,
      action,
      newItemsById,
      attachmentSelectors.getAttachmentsById(newGlobalState)
    );

    // Has Violated Dependencies
    newState = updateIsViolated(
      newState,
      newAndChangedIdSet,
      todayDate,
      newItemsById,
      prevItemsById,
      newDependenciesById,
      prevDependenciesById,
      newConstraintsById,
      prevConstraintsById,
      newGlobalState
    );

    // Initials
    newState = updateItemInitials(newState, newAndChangedIdSet, newItemsById, newTypesById, prevTypesById);

    // Is Completed
    newState = updateItemComplete(newState, action, newItemsById, newPropertiesById);
  }

  // Update related items that are drawn as identifiers in this object
  newState = updateRelatedItemsByType(
    newState,
    action,
    newAndChangedIdSet,
    newItemsById,
    prevItemsById,
    newRelationshipsById,
    prevRelationshipsById,
    newGlobalState
  );

  // Update blockingIdentifiers and blockedByIdentifiers
  newState = updateBlockIdentifiers(
    newState,
    action,
    newAndChangedIdSet,
    newItemsById,
    prevItemsById,
    newDependenciesById,
    prevDependenciesById,
    newGlobalState
  );

  // For now we will put Text Sizes here too... but we expect to move them into their own object
  // so that we aren't changing our core ItemDisplayRecord... this will lead to fewer unnecessary
  // renders.
  // Text Sizes

  newState = updateItemTextSizes(
    newState,
    prevState,
    newCalendar,
    todayDate,
    newFontSize,
    prevFontSize,
    newWidthMap,
    prevWidthMap,
    newSubwayLineLimit,
    prevSubwayLineLimit,
    newMobileWidth,
    prevMobileWidth,
    newIsMobilePortraitMode,
    prevIsMobilePortraitMode,
    newMobileShowDragHandles,
    prevMobileShowDragHandles
  );

  // Normal text sizes just require the state...
  // Mobile text sizes require: maxWidth, isMobileMode, showDragHandles

  // TODO: Handle mobile mode... but for now we will just do the normal Desktop/Tablet one

  // Now we need to double check:
  // New Items  <-- need to create everything for them

  // This should be separated out per individual view
  //   - excludedByFilter
  //   - index (used by Table, Relationship, Spreadsheet)
  //   - selected: false,

  return newState;
};

export default reducer;
